import * as THREE from 'three/webgpu';
import { AbstractEntitySelectionHandler, DatumReferencePriorityHitKind } from './AbstractEntitySelectionHandler';
import { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { ImportedModelTopologySelectionService } from '../../imported-model/selection/ImportedModelTopologySelectionService';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import {
    AssemblyPartDragOperationMode,
    AssemblyPartDragStrategy,
    ContourDragStrategy,
    isAssemblyPartDragStrategy,
    PointDragStrategy,
    type AbstractEntityDragStrategy,
} from '../drag';
import { AssemblyTransformToolMode } from '../../assembly/move-component/MoveComponentTypes';
import { AssemblyComponentFixationService } from '../../assembly/component-fixation';
import { AssemblyComponentDragCapabilityResolver } from '../../assembly/move-component/AssemblyComponentDragCapabilityResolver';
import { SketchDrawingTool } from '../../draw/DrawingSharedTypes';
import { isSketchContour } from '../../mesh';
import { isFeatureLine } from '../../mesh/feature';
import { SketchPointObject } from '../../mesh/sketch';

/**
 * 装配体实体选择处理器选项。
 */
export interface AssemblyEntitySelectionHandlerOptions {
    /** 是否启用装配体模式专属交互。 */
    assemblyModeEnabled: boolean;
}

/**
 * 装配体交互上下文。
 */
interface AssemblyPartInteractionContext {
    /** 当前交互应写入选择状态的最终命中。 */
    selectionPick: THREE.Intersection<THREE.Object3D>;
    /** 最终用于移动的根对象。 */
    transformRootObject: THREE.Object3D;
}

/**
 * 装配零件延迟拖拽上下文。
 */
interface PendingAssemblyPartDragContext {
    /** 鼠标按下时解析到的装配零件交互上下文。 */
    interactionContext: AssemblyPartInteractionContext;
    /** 启动拖拽时仍需使用的原始按下事件。 */
    startPayload: RawPointerEventPayload;
    /** 触发本次零部件交互的鼠标按钮。 */
    button: number;
    /** 鼠标移动事件中需要保持按下的按钮掩码。 */
    buttonMask: number;
    /** 本次拖拽超过阈值后应启动的零部件变换模式。 */
    operationMode: AssemblyPartDragOperationMode;
    /** 本次拖拽超过阈值后是否允许启动零部件变换。 */
    canStartComponentDrag: boolean;
}

/**
 * 装配体零部件 pending 拖拽阶段的相机控制挂起状态。
 */
interface PendingAssemblyPartDragCameraControlsSuspension {
    /** 挂起前相机控制器是否处于启用状态。 */
    controlsEnabledBeforeSuspend: boolean;
}

/**
 * 装配体模式实体选择处理器。
 *
 * 设计说明：
 * 1. 当前类统一承接 imported-model 的通用选择能力；
 * 2. 默认模式与装配体模式都复用同一个 handler 类，只通过配置决定是否启用装配体专属规则；
 * 3. 直接继承抽象选择基类，避免装配体空闲态隐式继承默认模式其它交互分支；
 * 4. 正式基准对象通过抽象基类受保护方法显式接入，保证基准面边线和基准点 hover 能进入统一状态；
 * 5. 基础点击、hover、拖拽与框选流程在本类中显式收口，便于后续装配体选择语义独立演进。
 */
export class AssemblyEntitySelectionHandler extends AbstractEntitySelectionHandler {
    /** 导入模型射线解析器。 */
    protected readonly importedModelRaycastResolver: ImportedModelRaycastResolver;
    /** 导入模型拓扑选择服务。 */
    protected readonly importedModelTopologySelectionService: ImportedModelTopologySelectionService;

    /** 当前实例是否启用装配体模式专属交互。 */
    private readonly assemblyModeEnabled: boolean;

    /** 等待移动阈值确认的装配零件拖拽上下文。 */
    private pendingAssemblyPartDragContext: PendingAssemblyPartDragContext | null = null;

    /** 右键命中可拖拽零部件但尚未超过阈值时的相机控制挂起状态。 */
    private pendingAssemblyPartDragCameraControlsSuspension: PendingAssemblyPartDragCameraControlsSuspension | null = null;

    /** 装配组件拖拽自由度能力解析服务。 */
    private readonly assemblyComponentDragCapabilityResolver: AssemblyComponentDragCapabilityResolver;

    /**
     * 创建导入模型选择处理器。
     *
     * @param entitySelectionManager 实体选择管理器。
     * @param importedModelRaycastResolver 导入模型射线解析器。
     * @param options 处理器行为选项。
     */
    public constructor(
        entitySelectionManager: EntitySelectionManager,
        importedModelRaycastResolver: ImportedModelRaycastResolver,
        options: AssemblyEntitySelectionHandlerOptions,
    ) {
        super(entitySelectionManager);
        this.importedModelRaycastResolver = importedModelRaycastResolver;
        this.importedModelTopologySelectionService = new ImportedModelTopologySelectionService({
            renderer: entitySelectionManager.renderer,
            importedModelRaycastResolver: this.importedModelRaycastResolver,
        });
        this.assemblyModeEnabled = options.assemblyModeEnabled;
        this.assemblyComponentDragCapabilityResolver = new AssemblyComponentDragCapabilityResolver(entitySelectionManager.renderer);
    }

    /** 结束本模式的拖拽借用和拓扑缓存，不销毁外部模型。 */
    public override dispose(): void {
        this.clearPendingAssemblyPartDragContext();
        this.importedModelTopologySelectionService.dispose();
        super.dispose();
    }

    /**
     * 判断鼠标按钮是否可以触发装配零部件交互。
     *
     * @param button 鼠标按钮编号。
     * @returns 左键或右键返回 `true`。
     */
    protected isAssemblyPartInteractionButton(button: number): boolean {
        return button === 0 || button === 2;
    }

    /**
     * 按鼠标按钮与当前装配体变换工具解析拖拽模式。
     *
     * 说明：
     * 1. 左键是功能菜单“移动零部件 / 旋转零部件”的主交互入口，必须尊重
     *    `MoveComponentController` 当前工具模式，避免显式旋转命令被选择层覆盖成移动；
     * 2. 右键到 Rotate 的映射暂时保留给后续恢复整零部件右键旋转能力；
     * 3. 当前是否真正启动拖拽由 `isAssemblyPartDragStartEnabledForButton` 统一判定。
     *
     * @param button 鼠标按钮编号。
     * @returns 本次拖拽应使用的移动或旋转模式。
     */
    protected resolveAssemblyPartDragOperationMode(button: number): AssemblyPartDragOperationMode {
        if (button === 2) {
            return AssemblyPartDragOperationMode.Rotate;
        }

        const toolMode = this.entitySelectionManager.renderer.moveComponentController?.getToolMode?.()
            ?? AssemblyTransformToolMode.Move;
        return toolMode === AssemblyTransformToolMode.Rotate
            ? AssemblyPartDragOperationMode.Rotate
            : AssemblyPartDragOperationMode.Move;
    }

    /**
     * 判断指定鼠标按钮是否允许在超过阈值后启动装配零部件拖拽。
     *
     * 设计说明：
     * 1. 右键单击仍需要走装配零部件命中选择，供后续 `contextmenu` 打开对应组件菜单；
     * 2. 右键只有在按下时已经命中装配零部件并超过阈值后才启动拖拽，空白右键拖动继续交给相机控制器；
     * 3. 右键拖拽必须复用既有 Rotate 映射和拖拽策略，避免选择层重新实现旋转语义。
     *
     * @param button 鼠标按钮编号。
     * @returns 是否允许启动装配零部件拖拽。
     */
    protected isAssemblyPartDragStartEnabledForButton(button: number): boolean {
        return button === 0 || button === 2;
    }

    /**
     * 按鼠标按钮解析移动事件中的按钮掩码。
     *
     * @param button 鼠标按钮编号。
     * @returns `MouseEvent.buttons` 对应的按钮掩码。
     */
    private resolveAssemblyPartButtonMask(button: number): number {
        return button === 2 ? 2 : 1;
    }

    /**
     * 扩展点击候选集。
     *
     * @returns 候选对象列表。
     */
    protected override collectClickSelectionTargets(): THREE.Object3D[] {
        return this.mergeSelectionTargets(
            this.collectDatumReferenceInteractionTargets(
                super.collectClickSelectionTargets(),
            ),
            this.importedModelRaycastResolver.getRaycastTargets(),
        );
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectClickSelectionTargets();
    }

    /**
     * 扩展悬停候选集。
     *
     * @returns 候选对象列表。
     */
    protected override collectHoverSelectionTargets(): THREE.Object3D[] {
        return this.mergeSelectionTargets(
            this.collectDatumReferenceInteractionTargets(
                super.collectHoverSelectionTargets(),
            ),
            this.importedModelRaycastResolver.getRaycastTargets(),
        );
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectHoverSelectionTargets();
    }

    /**
     * 扩展默认模式的拾取过滤规则。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 可参与射线拾取的候选对象列表。
     * @returns 命中结果；未命中时返回 null。
     */
    protected override pickEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        const entityPick = super.pickEntityAt(clientX, clientY, interactableObjects);
        const importedPick = this.pickImportedModelAt(clientX, clientY, interactableObjects);
        const datumPick = this.pickDatumReferenceEntityAt(clientX, clientY, interactableObjects);
        if (
            datumPick
            && this.resolveDatumPointOrAxisHitKind(datumPick.object) !== DatumReferencePriorityHitKind.None
        ) {
            return datumPick;
        }
        return this.resolveNearestPick([entityPick, importedPick, datumPick]);
    }

    //------ 装配体选择处理器基础交互 开始------
    /**
     * 处理鼠标按下。
     *
     * 说明：
     * 1. 装配体专属拖拽优先解析 imported-model 语义对象；
     * 2. 若最终命中是 FeatureFace / FeatureLine / FeaturePoint 代理，则选择状态必须保留该代理对象；
     * 3. 非装配体专属拖拽场景继续执行基础点拖拽、轮廓拖拽与框选起点记录；
     * 4. 本实现不依赖默认选择处理器，正式基准对象只通过抽象基类受保护方法显式接入装配体链路。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        const isAssemblyInteractionButton = this.isAssemblyPartInteractionButton(payload.button);
        if (payload.button !== 0 && !isAssemblyInteractionButton) {
            return;
        }
        if (this.entitySelectionManager.isSketchDrawingActive()) {
            return;
        }

        if (this.handleMoveComponentCollisionCheckComponentSelection(payload)) {
            return;
        }

        if (this.assemblyModeEnabled && isAssemblyInteractionButton) {
            const interactionContext = this.resolveAssemblyPartInteractionContext(payload);

            if (interactionContext) {
                const operationMode = this.resolveAssemblyPartDragOperationMode(payload.button);
                const canStartComponentDrag = this.isAssemblyPartDragStartEnabledForButton(payload.button);
                this.pendingAssemblyPartDragContext = {
                    interactionContext,
                    startPayload: payload,
                    button: payload.button,
                    buttonMask: this.resolveAssemblyPartButtonMask(payload.button),
                    operationMode,
                    canStartComponentDrag,
                };
                this.suspendCameraControlsForPendingAssemblyPartDrag(
                    payload.button,
                    interactionContext,
                    canStartComponentDrag,
                );
                this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
                this.entitySelectionManager.setIsBoxDragging(false);
                this.entitySelectionManager.setBoxDragEnd(null);
                this.resetSelectionBoxVisual();
                this.entitySelectionManager.setSuppressNextClick(false);
                if (this.entitySelectionManager.getPrimarySelectedObject()?.uuid !== interactionContext.selectionPick.object.uuid) {
                    this.entitySelectionManager.updateSelectionState(
                        this.resolveNextSelectionState(this.toIntersections([interactionContext.selectionPick.object]), false),
                        undefined,
                        undefined,
                        { intent: 'pointer-down' },
                    );
                }
                return;
            }

            this.clearPendingAssemblyPartDragContext();
            if (payload.button !== 0) {
                return;
            }
        }

        if (payload.button !== 0) {
            return;
        }

        const picked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );

        // Ctrl+左键用于点击增量选择，不启动点拖拽或轮廓拖拽。
        if (payload.ctrlKey) {
            this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        if (picked?.object instanceof SketchPointObject) {
            if (
                this.entitySelectionManager.renderer.sketchDrawingManager?.isDrawingActive()
                && (this.entitySelectionManager.renderer.sketchDrawingManager?.getCurrentTool() || null) === SketchDrawingTool.Point
            ) {
                return;
            }

            const pointObject = picked.object;
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);

            if (this.entitySelectionManager.getCurrentSelectedPoint() !== pointObject) {
                this.entitySelectionManager.updateSelectionState(
                    this.resolveNextSelectionState(this.toIntersections([pointObject]), false),
                );
            }

            if (!pointObject.canDrag() || !this.isPointDragEnabled()) {
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }

            this.startDrag(new PointDragStrategy(this.entitySelectionManager.renderer, pointObject), payload);
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }

        if (picked && isSketchContour(picked.object)) {
            const contour = picked.object;
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);

            if (this.isContourDragEnabled()) {
                this.startDrag(new ContourDragStrategy(this.entitySelectionManager.renderer, contour), payload);
            }
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }

        this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(false);
    }

    /**
     * 处理鼠标移动。
     *
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (activeDragStrategy) {
            activeDragStrategy.update(payload);
            return;
        }

        if (this.tryStartPendingAssemblyPartDrag(payload)) {
            return;
        }

        const mouseDownStart = this.entitySelectionManager.getMouseDownStart();
        if (this.isBoxSelectionEnabled() && mouseDownStart && payload.buttons !== 0) {
            const dx = payload.clientX - mouseDownStart.x;
            const dy = payload.clientY - mouseDownStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > this.entitySelectionManager.getBoxDragThreshold() || this.entitySelectionManager.getIsBoxDragging()) {
                this.entitySelectionManager.setIsBoxDragging(true);
                this.entitySelectionManager.setBoxDragEnd(new THREE.Vector2(payload.clientX, payload.clientY));
                this.clearHoverState();
                this.updateSelectionBoxVisual(mouseDownStart, new THREE.Vector2(payload.clientX, payload.clientY));
                return;
            }
        }

        // 禁用框选的模式中，超过拖拽阈值后只抑制 click，避免释放时误触发选择。
        if (!this.isBoxSelectionEnabled() && mouseDownStart && payload.buttons !== 0) {
            const dx = payload.clientX - mouseDownStart.x;
            const dy = payload.clientY - mouseDownStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > this.entitySelectionManager.getBoxDragThreshold()) {
                this.entitySelectionManager.setSuppressNextClick(true);
            }
        }

        if (payload.buttons === 0 && this.entitySelectionManager.getMouseDownStart()) {
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
        }

        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /**
     * 处理鼠标抬起。
     *
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        if (payload.button !== 0 && !this.isAssemblyPartInteractionButton(payload.button)) {
            return;
        }

        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (activeDragStrategy) {
            if (payload.button !== 0 && !isAssemblyPartDragStrategy(activeDragStrategy)) {
                return;
            }
            this.stopDrag();
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(true);
            return;
        }

        if (this.pendingAssemblyPartDragContext) {
            if (this.pendingAssemblyPartDragContext.button !== payload.button) {
                return;
            }
            this.resetPendingAssemblyPartDragSession();
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        if (payload.button !== 0) {
            return;
        }

        const dragStart = this.entitySelectionManager.getMouseDownStart();
        if (!dragStart) {
            return;
        }

        const isBoxDragging = this.entitySelectionManager.getIsBoxDragging();
        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();

        if (isBoxDragging && this.isBoxSelectionEnabled()) {
            this.performBoxSelection(dragStart, new THREE.Vector2(payload.clientX, payload.clientY), false);
            this.entitySelectionManager.setSuppressNextClick(true);
        }
    }

    /**
     * 处理视口边界或窗口状态导致的鼠标会话取消。
     *
     * 设计说明：
     * 装配体拖拽存在“按下后等待移动阈值”的 pending 状态；离开 canvas 时必须按取消处理，
     * 不能再让后续浏览器 click/contextmenu 把它解释成一次有效单击。
     *
     * @param payload 原始事件。
     */
    public override onPointerCancel(payload: RawPointerEventPayload): void {
        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (activeDragStrategy) {
            this.stopDrag();
        }
        this.clearPendingAssemblyPartDragContext();
        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(true);
        payload.nativeEvent.preventDefault();
    }

    /**
     * 处理鼠标点击。
     *
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.isSketchDrawingActive()) {
            return;
        }
        this.clearPendingAssemblyPartDragContext();
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );
        const picked = this.resolveClickSelectionPick(rawPicked);

        if (
            picked?.object instanceof SketchPointObject
            && (this.entitySelectionManager.renderer.sketchDrawingManager?.isDrawingActive() || false)
            && (this.entitySelectionManager.renderer.sketchDrawingManager?.getCurrentTool() || null) === SketchDrawingTool.Point
        ) {
            return;
        }

        if (picked && isSketchContour(picked.object)) {
            return;
        }

        if (picked) {
            this.entitySelectionManager.updateSelectionState(this.resolveNextSelectionState([picked], payload.ctrlKey));
            return;
        }

        if (payload.ctrlKey) {
            return;
        }

        this.entitySelectionManager.updateSelectionState([]);
    }
    //------ 装配体选择处理器基础交互 结束------

    /**
     * 装配体模式禁用框选。
     *
     * 设计说明：
     * 1. 装配体环境当前以零部件点击选择为主；
     * 2. 禁用框选可以避免与装配体操作、拖拽和后续配合候选语义冲突；
     * 3. 点击选择与移入高亮仍保留默认 imported-model 行为。
     *
     * @returns 始终返回 `false`。
     */
    protected override isBoxSelectionEnabled(): boolean {
        return this.assemblyModeEnabled ? false : super.isBoxSelectionEnabled();
    }

    /**
     * 默认实例框选只更新选择状态，不派发框选业务事件。
     *
     * @param selectedObjects 本次框选命中对象。
     * @param isCrossing 是否 crossing 框选。
     * @param selectionRect 框选屏幕矩形。
     */
    protected override publishBoxSelectionEvents(
        selectedObjects: THREE.Object3D[],
        isCrossing: boolean,
        selectionRect: { minX: number; minY: number; maxX: number; maxY: number },
    ): void {
        void selectedObjects;
        void isCrossing;
        void selectionRect;
    }

    /**
     * 收集框选候选对象。
     *
     * @returns 候选对象列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return super.collectBoxSelectionTargets().filter(isFeatureLine);
    }

    /**
     * 拾取导入模型对象。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 当前候选对象。
     * @returns 导入模型命中；未命中时返回 null。
     */
    protected pickImportedModelAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        return this.importedModelTopologySelectionService.pickAt({
            clientX,
            clientY,
            interactableObjects,
            selectionMode: this.entitySelectionManager.getSelectionMode(),
            allowOccludedSelection: this.entitySelectionManager.getAllowOccludedFeatureTopologySelection(),
        });
    }

    /**
     * 拾取装配体选择链路下的正式基准对象。
     *
     * 设计说明：
     * 1. 正式基准对象不属于 imported-model，也不会被统一实体拾取 helper 直接消费；
     * 2. 装配体空闲态需要显式接入该拾取入口，才能让基准面边线与基准点产生 hover 事件；
     * 3. 命中结果继续归一化为正式基准对象自身，后续视觉状态只依赖统一的 hoveredObject。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 当前候选对象。
     * @returns 基准命中；未命中时返回 `null`。
     */
    private pickDatumReferenceEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        return this.pickDatumReferenceAt(clientX, clientY, interactableObjects);
    }

    /**
     * 从多个命中结果中解析距离最近者。
     *
     * @param picks 候选命中结果。
     * @returns 最近命中；全部为空时返回 `null`。
     */
    private resolveNearestPick(
        picks: Array<THREE.Intersection<THREE.Object3D> | null>,
    ): THREE.Intersection<THREE.Object3D> | null {
        return picks.reduce<THREE.Intersection<THREE.Object3D> | null>((nearestPick, currentPick) => {
            if (!currentPick) {
                return nearestPick;
            }
            if (!nearestPick) {
                return currentPick;
            }
            return currentPick.distance < nearestPick.distance ? currentPick : nearestPick;
        }, null);
    }

    /**
     * 按当前鼠标位移尝试启动待确认的装配零件拖拽。
     *
     * @param payload 鼠标移动事件载荷。
     * @returns 是否已经启动拖拽并消费本次移动事件。
     */
    private tryStartPendingAssemblyPartDrag(payload: RawPointerEventPayload): boolean {
        const pendingContext = this.pendingAssemblyPartDragContext;
        if (!pendingContext) {
            return false;
        }

        const mouseDownStart = this.entitySelectionManager.getMouseDownStart();
        if (!mouseDownStart) {
            this.clearPendingAssemblyPartDragContext();
            return false;
        }

        if ((payload.buttons & pendingContext.buttonMask) !== pendingContext.buttonMask) {
            this.clearPendingAssemblyPartDragContext();
            return false;
        }

        const dx = payload.clientX - mouseDownStart.x;
        const dy = payload.clientY - mouseDownStart.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance <= this.entitySelectionManager.getBoxDragThreshold()) {
            return false;
        }

        const { interactionContext, startPayload } = pendingContext;
        this.resetPendingAssemblyPartDragSession();

        if (!pendingContext.canStartComponentDrag) {
            this.clearHoverState();
            return false;
        }

        const operationMode = this.resolveAuthorizedAssemblyPartDragOperationMode(pendingContext);
        if (!operationMode) {
            this.clearHoverState();
            return false;
        }

        /**
         * 右键旋转成立后，选择、高亮、特征树和历史目标必须统一到同一个组件根。
         * mousedown 阶段仍保留精确拓扑对象供单击菜单使用；超过阈值进入旋转后不再让拓扑代理
         * 与业务 transform 根并行存在，避免多 Mesh Servo 选中包根、直接 Mesh 出现双重视觉。
         */
        const dragSelectionObject = operationMode === AssemblyPartDragOperationMode.Rotate
            ? interactionContext.transformRootObject
            : interactionContext.selectionPick.object;
        if (this.entitySelectionManager.getPrimarySelectedObject()?.uuid !== dragSelectionObject.uuid) {
            this.entitySelectionManager.updateSelectionState(
                this.resolveNextSelectionState(this.toIntersections([dragSelectionObject]), false),
                undefined,
                undefined,
                { intent: 'drag-start' },
            );
        }

        // 只有确认发生拖拽后才清理 hover 与压制 click，避免单击选择被拖拽前置逻辑吞掉。
        this.clearHoverState();
        this.startDrag(new AssemblyPartDragStrategy(
            this.entitySelectionManager.renderer,
            interactionContext.transformRootObject,
            null,
            operationMode,
        ), startPayload);
        this.entitySelectionManager.setSuppressNextClick(true);
        this.entitySelectionManager.getActiveDragStrategy()?.update(payload);
        payload.nativeEvent.preventDefault();
        return true;
    }

    /**
     * 在右键命中可拖拽零部件的 pending 阶段提前挂起相机控制。
     *
     * 设计说明：
     * 1. 右键按下后 CameraControls 会立即进入视角旋转状态，若等到超过阈值再禁用，相机会先变化几帧；
     * 2. 只有右键命中可变换零部件时才挂起相机，空白右键拖动仍保持视角旋转；
     * 3. 真正启动零部件拖拽前会恢复控制权，再交给 `AssemblyPartDragStrategy` 的既有生命周期统一禁用和恢复。
     *
     * @param button 鼠标按钮编号。
     * @param interactionContext 装配体零部件交互上下文。
     * @param canStartComponentDrag 当前按钮是否允许启动零部件拖拽。
     */
    /**
     * 解析通过自由度校验后的装配零部件拖拽模式。
     *
     * @param pendingContext 等待超过拖拽阈值的装配零部件拖拽上下文。
     * @returns 可启动的拖拽模式；为 `null` 时当前目标不应启动零部件拖拽。
     */
    private resolveAuthorizedAssemblyPartDragOperationMode(
        pendingContext: PendingAssemblyPartDragContext,
    ): AssemblyPartDragOperationMode | null {
        return this.resolveAuthorizedAssemblyPartDragOperationModeForTarget({
            targetObject: pendingContext.interactionContext.transformRootObject,
            requestedOperationMode: pendingContext.operationMode,
            button: pendingContext.button,
        });
    }

    /**
     * 按目标组件和鼠标意图解析通过自由度授权后的装配拖拽模式。
     *
     * 设计说明：
     * 默认装配左键拖拽与“移动零部件”命令态左键拖拽必须共享同一套 Move -> Rotate 自适应规则。
     * 该方法是选择层唯一的自由度授权入口，子类只能传入目标对象和原始意图，不能自行分叉判断。
     *
     * @param input 授权解析输入。
     * @returns 可启动的拖拽模式；为 `null` 时当前目标不应启动零部件拖拽。
     */
    protected resolveAuthorizedAssemblyPartDragOperationModeForTarget(input: {
        readonly targetObject: THREE.Object3D;
        readonly requestedOperationMode: AssemblyPartDragOperationMode;
        readonly button: number;
    }): AssemblyPartDragOperationMode | null {
        const requestedToolMode = this.toAssemblyTransformToolMode(input.requestedOperationMode);
        const capability = this.assemblyComponentDragCapabilityResolver.resolveDragCapability({
            targetObject: input.targetObject as never,
            requestedToolMode,
            /**
             * SOLIDWORKS 默认左键与“移动零部件”命令优先平移；当配合/固定关系只剩旋转自由度时，
             * 左键拖拽应自动切换为旋转。右键和显式“旋转零部件”只请求 Rotate，不反向降级为 Move。
             */
            allowRotateFallbackWhenMoveBlocked: input.button === 0
                && requestedToolMode === AssemblyTransformToolMode.Move,
        });

        return this.toAssemblyPartDragOperationMode(capability.resolvedToolMode);
    }

    /**
     * 将选择层拖拽模式转换为装配工具模式。
     *
     * @param operationMode 选择层拖拽模式。
     * @returns 装配工具模式。
     */
    private toAssemblyTransformToolMode(
        operationMode: AssemblyPartDragOperationMode,
    ): AssemblyTransformToolMode {
        return operationMode === AssemblyPartDragOperationMode.Rotate
            ? AssemblyTransformToolMode.Rotate
            : AssemblyTransformToolMode.Move;
    }

    /**
     * 将装配工具模式转换为选择层拖拽模式。
     *
     * @param toolMode 装配工具模式。
     * @returns 选择层拖拽模式；为 `null` 时不启动拖拽。
     */
    private toAssemblyPartDragOperationMode(
        toolMode: AssemblyTransformToolMode | null,
    ): AssemblyPartDragOperationMode | null {
        if (!toolMode) {
            return null;
        }
        return toolMode === AssemblyTransformToolMode.Rotate
            ? AssemblyPartDragOperationMode.Rotate
            : AssemblyPartDragOperationMode.Move;
    }

    /**
     * 在右键命中可拖拽零部件的 pending 阶段提前挂起相机控制。
     *
     * @param button 鼠标按钮编号。
     * @param interactionContext 装配零部件交互上下文。
     * @param canStartComponentDrag 当前按钮是否允许启动零部件拖拽。
     */
    private suspendCameraControlsForPendingAssemblyPartDrag(
        button: number,
        interactionContext: AssemblyPartInteractionContext,
        canStartComponentDrag: boolean,
    ): void {
        this.restorePendingAssemblyPartDragCameraControls();
        if (
            button !== 2
            || !canStartComponentDrag
            || !AssemblyComponentFixationService.canTransformSceneObject(interactionContext.transformRootObject)
        ) {
            return;
        }

        const cameraManager = this.entitySelectionManager.renderer.cameraManager;
        const controls = cameraManager?.controls;
        if (!cameraManager || !controls) {
            return;
        }

        this.pendingAssemblyPartDragCameraControlsSuspension = {
            controlsEnabledBeforeSuspend: controls.enabled,
        };
        cameraManager.cancelControlsInteraction();
        if (controls.enabled) {
            cameraManager.setControlsEnabled(false);
        }
    }

    /**
     * 恢复 pending 拖拽阶段提前挂起的相机控制。
     *
     * 说明：
     * 如果挂起前相机本来就是禁用状态，这里只清理记录，不强行启用，避免覆盖外部工具的控制权。
     */
    private restorePendingAssemblyPartDragCameraControls(): void {
        const suspension = this.pendingAssemblyPartDragCameraControlsSuspension;
        this.pendingAssemblyPartDragCameraControlsSuspension = null;
        if (!suspension?.controlsEnabledBeforeSuspend) {
            return;
        }

        const cameraManager = this.entitySelectionManager.renderer.cameraManager;
        cameraManager?.cancelControlsInteraction();
        cameraManager?.setControlsEnabled(true);
    }

    /**
     * 清理等待移动阈值确认的装配零件拖拽上下文。
     */
    private clearPendingAssemblyPartDragContext(): void {
        this.restorePendingAssemblyPartDragCameraControls();
        this.pendingAssemblyPartDragContext = null;
    }

    /**
     * 清理等待移动阈值确认的装配零件拖拽会话状态。
     *
     * 说明：
     * 右键拖拽被暂时降级为视角旋转时也必须走同一清理链路，避免残留 `mouseDownStart`
     * 影响下一次左键选择或框选判定。
     */
    private resetPendingAssemblyPartDragSession(): void {
        this.clearPendingAssemblyPartDragContext();
        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
    }

    /**
     * 解析当前装配体交互应使用的“选择对象 + 拖拽目标”上下文。
     *
     * @param payload 原始事件。
     * @returns 交互上下文；当前不应启动零件拖拽时返回 `null`。
     */
    protected resolveAssemblyPartInteractionContext(payload: RawPointerEventPayload): AssemblyPartInteractionContext | null {
        if (!this.isAssemblyPartInteractionButton(payload.button) || payload.ctrlKey) {
            return null;
        }
        if (this.entitySelectionManager.isSketchDrawingActive()) {
            return null;
        }
        const operationMode = this.resolveAssemblyPartDragOperationMode(payload.button);
        if (!this.isAssemblyPartDragEnabled(operationMode)) {
            return null;
        }

        const interactableObjects = this.collectClickSelectionTargets();
        const selectionPick = this.pickImportedModelAt(
            payload.clientX,
            payload.clientY,
            interactableObjects,
        );
        if (!selectionPick) {
            return null;
        }

        const transformRootObject = this.importedModelRaycastResolver.resolveTransformRootObject(selectionPick.object);
        if (!transformRootObject) {
            return null;
        }

        return {
            selectionPick,
            transformRootObject,
        };
    }

    //------ 移动旋转零部件碰撞检查选项 开始------
    /**
     * 处理“供碰撞检查的零部件”选择。
     *
     * 设计说明：
     * 1. 旋转零部件命令仍运行在 Assembly 选择处理器内，移动零部件命令运行在 MoveComponent 选择处理器内；
     * 2. 因此该分支收口在装配体选择处理器基类中，由两个命令共享；
     * 3. 选择阶段只写入碰撞检查候选列表，不启动装配体拖拽，必须等待 PropertyManager 的“恢复拖动”按钮切回拖拽阶段。
     *
     * @param payload 原始鼠标事件。
     * @returns 当前事件是否已由碰撞检查选择阶段处理。
     */
    protected handleMoveComponentCollisionCheckComponentSelection(payload: RawPointerEventPayload): boolean {
        const controller = this.entitySelectionManager.renderer.moveComponentController;
        if (!controller?.isCollisionCheckComponentSelectionActive?.()) {
            return false;
        }
        if (payload.button !== 0) {
            return false;
        }

        const selectionPick = this.pickImportedModelAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );
        const transformRootObject = selectionPick
            ? this.importedModelRaycastResolver.resolveTransformRootObject(selectionPick.object)
            : null;

        if (transformRootObject) {
            controller.addCollisionCheckComponentObject(transformRootObject);
        }

        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(true);
        this.restoreMoveComponentCollisionCheckSelectionState();
        this.entitySelectionManager.renderer.requestRender?.();
        payload.nativeEvent.preventDefault();
        return true;
    }

    /**
     * 恢复碰撞检查零部件选择高亮。
     */
    private restoreMoveComponentCollisionCheckSelectionState(): void {
        const controller = this.entitySelectionManager.renderer.moveComponentController;
        const selectedObjects = controller?.getCollisionCheckComponentObjects?.() ?? [];
        this.entitySelectionManager.updateSelectionState(
            selectedObjects.length > 0 ? this.toIntersections(selectedObjects) : [],
        );
    }
    //------ 移动旋转零部件碰撞检查选项 结束------

    /**
     * 启动拖拽策略。
     *
     * @param strategy 拖拽策略实例。
     * @param payload 拖拽启动事件载荷。
     */
    protected startDrag(strategy: AbstractEntityDragStrategy, payload: RawPointerEventPayload): void {
        this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(true);
        try {
            strategy.start(payload);
            this.entitySelectionManager.setActiveDragStrategy(strategy);
        } catch (error) {
            this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(false);
            throw error;
        }
    }

    /**
     * 停止当前拖拽策略。
     */
    private stopDrag(): void {
        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (!activeDragStrategy) {
            return;
        }
        try {
            const affectedSketchId = activeDragStrategy.resolveAffectedSketchId();
            const hasGeometryChanged = activeDragStrategy.hasGeometryChanged();
            activeDragStrategy.stop();
            if (hasGeometryChanged && affectedSketchId) {
                this.entitySelectionManager.renderer.sketchGroupManager?.getGroupById(affectedSketchId)?.commitDefinitionChanges({
                    reason: 'drag-finished',
                });
            }
            this.entitySelectionManager.setActiveDragStrategy(null);
        } finally {
            this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(false);
        }
    }

    /**
     * 判断装配体零件拖拽当前是否可用。
     *
     * 设计说明：
     * 1. Assembly 空闲态的默认 Move 能力与显式“移动零部件 / 旋转零部件”命令都允许进入整零部件拖拽；
     * 2. 具体授权规则统一收口到 MoveComponentController，避免选择层散落激活来源判断；
     * 3. 整零部件拖拽继续复用 controller 的主模式、轴向约束与碰撞检测能力。
     *
     * @returns 当前是否允许进入装配体零件拖拽。
     */
    private isAssemblyPartDragEnabled(operationMode: AssemblyPartDragOperationMode): boolean {
        const controller = this.entitySelectionManager.renderer.moveComponentController;
        const requestedToolMode = operationMode === AssemblyPartDragOperationMode.Rotate
            ? AssemblyTransformToolMode.Rotate
            : AssemblyTransformToolMode.Move;
        return controller?.canStartViewportDrag?.(requestedToolMode) === true;
    }

    /**
     * 合并默认候选集与 imported-model 候选集。
     *
     * @param baseTargets 默认候选集。
     * @param importedTargets imported-model 候选集。
     * @returns 去重后的候选集。
     */
    private mergeSelectionTargets(
        baseTargets: THREE.Object3D[],
        importedTargets: readonly THREE.Object3D[],
    ): THREE.Object3D[] {
        const targetMap = new Map<string, THREE.Object3D>();
        baseTargets.forEach((target) => {
            targetMap.set(target.uuid, target);
        });
        importedTargets.forEach((target) => {
            if (!targetMap.has(target.uuid)) {
                targetMap.set(target.uuid, target);
            }
        });
        return Array.from(targetMap.values());
    }
}
